import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import Formatted from '@flipster/react/design-utils/formatted';
import { useRafThrottledAtomValue } from '@flipster/react/misc/state/use-throttled-atom-value';
import { css, cx } from '@flipster/styled-system/css';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { formBunja } from './bunja/form';
import { realtimeStateBunja } from './bunja/price';
import { col, info, row } from './styles';

const OrderSizeInfo = () => {
  const transTrade = useTranslations('Trade.r250401');
  const { symbolInfoAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const { askAmountAtom, bidAmountAtom } = useBunja(realtimeStateBunja);
  const { reduceOnlyMaxAtom } = useBunja(formBunja);
  const throttledAskAmount = useRafThrottledAtomValue(askAmountAtom);
  const throttledBidAmount = useRafThrottledAtomValue(bidAmountAtom);
  const reduceOnlyMax = useAtomValue(reduceOnlyMaxAtom);

  if (!symbolInfo) return null;

  const quoteCurrency = symbolInfo.quoteCurrency;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
  // In reduce-only mode the order is bounded by the current position size, not the symbol-level notional max.
  const maxOrderAmount = reduceOnlyMax ? reduceOnlyMax.amount : symbolInfo.notionalMaxOrderAmount;

  return (
    <div className={info}>
      <p
        className={css({
          textStyle: 'Text/M/Strong',
          mb: 4,
          color: 'content.default.level-3',
        })}
      >
        {transTrade('Order_value_and_max_label')}
      </p>
      <div className={row}>
        <div className={cx(col, css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.accent.buy' }))}>
          <Formatted.Number value={throttledAskAmount} precision={quoteCurrencyPrecision} toFixed withColor />
          <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.accent.buy' })}>
            <span>/ </span>
            <Formatted.Number
              value={maxOrderAmount}
              precision={quoteCurrencyPrecision}
              toFixed
              suffix={quoteCurrency}
            />
          </p>
        </div>
        <div className={cx(col, css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.accent.sell' }))}>
          <Formatted.Number value={`-${throttledBidAmount}`} precision={quoteCurrencyPrecision} toFixed withColor abs />
          <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.accent.sell' })}>
            <span>/ </span>
            <Formatted.Number
              value={maxOrderAmount}
              precision={quoteCurrencyPrecision}
              toFixed
              suffix={quoteCurrency}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderSizeInfo;
